import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/components/layout/Toast'
import type { Cotizacion, Empresa, CotizacionItemsTareas, CotizacionTarea } from '@/lib/types'
import { formatCurrency, formatDate, fmtPct, r2, processContent, sanitizeHtml } from '@/lib/utils'
import TareaDetalleInline from '@/components/cotizaciones/TareaDetalleInline'
import TaskDetailPages from '@/components/cotizaciones/TaskDetailPages'
import { DatosBancarios } from '@/components/cotizaciones/DatosBancarios'
import GenerarCotizacionModal from '@/components/cotizaciones/GenerarCotizacionModal'
import { IconBarChart, IconCheckCircle, IconFileText, IconList, IconTimer, IconZap, IconDollar, IconUser, IconPackage, IconWallet, IconEdit, IconArrowLeft, IconDownload, IconWhatsApp, IconCopy } from '@/components/icons/Icons'
import { PageHeader, Breadcrumbs } from '@/components/ui'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

type TabId = 'resumen' | 'tareas' | 'documento'

export default function DetalleCotTareas() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const db = useSupabase()
  const api = useApi()
  const { token: authToken } = useAuth()
  const pdfRef = useRef<HTMLDivElement>(null)

  const [cot, setCot] = useState<Cotizacion | null>(null)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabId>('resumen')
  const [expandedTarea, setExpandedTarea] = useState<number | null>(null)
  const [exportStep, setExportStep] = useState(-1)
  const cancelledRef = useRef(false)
  const [sendingWA, setSendingWA] = useState(false)
  const [showWAModal, setShowWAModal] = useState(false)
  const [waPhone, setWaPhone] = useState('')
  const [showGenerarModal, setShowGenerarModal] = useState(false)
  const [cotHijas, setCotHijas] = useState<Cotizacion[]>([])
  const [duplicating, setDuplicating] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const [emp, c] = await Promise.all([
          db.getAll<Empresa>('empresa'),
          db.getById<Cotizacion>('cotizaciones', parseInt(id!)),
        ])
        setEmpresa(emp[0] || null)
        setCot(c)
        if (!c) { showToast('Cotización no encontrada', 'error'); nav('/cotizaciones') }
        if (c && c.tipo_cotizacion !== 'tareas') {
          // Redirect non-tareas to VerCotizacion
          const path = c.tipo_cotizacion === 'equipo_dedicado' ? `/equipo/${c.id}` : `/cotizaciones/${c.id}`
          nav(path, { replace: true })
        }
      } catch { showToast('Error al cargar', 'error') }
      finally { setLoading(false) }
    })()
  }, [id])

  // Load child quotations generated from this task quotation
  useEffect(() => {
    if (!id) return
    ;(async () => {
      try {
        const all = await db.getAll<Cotizacion>('cotizaciones')
        setCotHijas(all.filter(c => c.cotizacion_origen_id === parseInt(id!)))
      } catch { /* non-critical */ }
    })()
  }, [id])

  // Extract tareas data
  const tareasData = cot?.items && typeof cot.items === 'object' && 'tareas' in cot.items
    ? cot.items as CotizacionItemsTareas : null
  const tareasItems: CotizacionTarea[] = tareasData?.tareas ?? []

  // Get total MTok from a task (new costos_items or legacy costos)
  const getTotalMTok = (t: CotizacionTarea): number | null => {
    if (t.costos_items?.length) {
      const total = t.costos_items
        .filter(c => c.unidad === 'MTok')
        .reduce((sum, c) => sum + c.cantidad, 0)
      return total > 0 ? r2(total) : null
    }
    if (!t.costos) return null
    const total = t.costos.tokens_input_mtok + t.costos.tokens_thinking_mtok + t.costos.tokens_output_mtok
    return total > 0 ? r2(total) : null
  }

  // KPIs
  const totalTareas = tareasItems.length
  const totalHoras = tareasItems.reduce((s, t) => s + (t.horas_estimadas ?? 0), 0)
  const totalTokens = tareasItems.reduce((s, t) => s + (getTotalMTok(t) ?? 0), 0)

  // PDF generation via backend
  const exportPDF = async () => {
    if (!cot) return
    cancelledRef.current = false
    setExportStep(0)
    const prevTab = activeTab
    try {
      // Step 0: Generate PDF on backend (Gotenberg → R2)
      const result = await api.post<{ ok: boolean; url: string; version: number; r2Key: string; sizeBytes: number }>(
        `/api/pdf/generate/cotizacion/${cot.id}`, {}
      )
      if (!result.ok) throw new Error('No se pudo generar el PDF')
      if (cancelledRef.current) throw new Error('Exportación cancelada')

      // Step 1-2: Upload + DB update already handled by backend
      setExportStep(2)
      setCot(prev => prev ? { ...prev, pdf_url: result.url } : prev)

      // Step 3: Download via proxy endpoint
      setExportStep(3)
      const downloadUrl = `${API_URL}/api/pdf/download/cotizacion/${cot.id}?v=${result.version}`
      const res = await fetch(downloadUrl, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
      })
      if (!res.ok) throw new Error('Error al descargar PDF')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${cot?.proyecto_nombre || 'Cotizacion'} - ${cot?.cliente_nombre || cot?.numero}.pdf`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)

      // Step 4: Done
      setExportStep(4)
      showToast('PDF descargado ✓', 'success')
      await new Promise(r => setTimeout(r, 600))
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'desconocido'
      if (msg !== 'Exportación cancelada') showToast('Error al exportar: ' + msg, 'error')
      else showToast('Exportación cancelada', 'info')
    } finally {
      setExportStep(-1)
      if (prevTab !== 'documento') setActiveTab(prevTab)
    }
  }

  // WhatsApp send
  const sendWhatsApp = async () => {
    if (!cot || !waPhone.trim()) return
    setSendingWA(true)
    showToast('Generando PDF y enviando por WhatsApp…', 'info')
    const prevTab = activeTab
    try {
      // Generate PDF on backend (or use existing pdf_url)
      let pdfUrl = cot.pdf_url
      if (!pdfUrl) {
        const pdfResult = await api.post<{ ok: boolean; url: string }>(
          `/api/pdf/generate/cotizacion/${cot.id}`, {}
        )
        if (!pdfResult.ok) throw new Error('No se pudo generar el PDF')
        pdfUrl = pdfResult.url
        setCot(prev => prev ? { ...prev, pdf_url: pdfUrl! } : prev)
      }

      const portalUrl = cot.public_token ? `${window.location.origin}/ver/${cot.public_token}` : undefined
      let phone = waPhone.trim().replace(/[^0-9]/g, '')
      if (phone.startsWith('0')) phone = '51' + phone.slice(1)
      if (phone.length <= 9) phone = '51' + phone

      const downloadUrl = `${API_URL}/api/pdf/download/cotizacion/${cot.id}`

      const res = await fetch(
        `${API_URL}/api/notifications/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            evento: 'cotizacion_enviada',
            variables: { nombre: cot.cliente_contacto || cot.cliente_nombre || 'Cliente', numero: cot.numero, monto: formatCurrency(cot.total, cot.moneda), url: portalUrl || downloadUrl },
            telefono: phone, empresa_id: cot.cliente_id || null, contentUri: downloadUrl,
          }),
        }
      )
      const result = await res.json()
      if (!res.ok || !result.success) throw new Error(result.error || 'Error al enviar notificación')
      showToast('Cotización enviada por WhatsApp ✓', 'success')
      setShowWAModal(false); setWaPhone('')
    } catch (e) { showToast('Error: ' + (e instanceof Error ? e.message : 'desconocido'), 'error') }
    finally { setSendingWA(false); if (prevTab !== 'documento') setActiveTab(prevTab) }
  }

  /** Duplicate this cotización as a new borrador */
  const handleDuplicate = async () => {
    if (!cot) return
    setDuplicating(true)
    try {
      const { id: _id, numero: _num, public_token: _tok, pdf_url: _pdf, created_at: _ca, cotizacion_origen_id: _ori, tareas_seleccionadas: _ts, ...rest } = cot as Record<string, unknown> & Cotizacion
      const today = new Date().toISOString().slice(0, 10)
      const data: Record<string, unknown> = {
        ...rest,
        estado: 'borrador',
        fecha_emision: today,
        proyecto_nombre: cot.proyecto_nombre ? `${cot.proyecto_nombre} (Copia)` : null,
      }
      const newId = await db.add('cotizaciones', data)
      showToast('Cotización duplicada ✓', 'success')
      nav(`/tareas-cot/${newId}`)
    } catch (e) {
      showToast('Error al duplicar: ' + (e instanceof Error ? e.message : 'desconocido'), 'error')
    } finally {
      setDuplicating(false)
    }
  }

  // Mermaid init — must be before loading guard to keep hook count stable
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const m = (await import('mermaid')).default
        if (!cancelled) m.initialize({ startOnLoad: false, theme: 'base', themeVariables: { primaryColor: '#4F7FE8', primaryBorderColor: '#1C4587', lineColor: '#1C4587', secondaryColor: '#eef1f8', tertiaryColor: '#f8f9fc' } })
      } catch (e) { console.warn('[mermaid] init error:', e) }
    })()
    return () => { cancelled = true }
  }, [])

  // Mermaid render — must be before loading guard to keep hook count stable
  useEffect(() => {
    if (!cot) return
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const els = document.querySelectorAll('.cot-mermaid-container .mermaid')
        if (!els || els.length === 0 || cancelled) return
        console.warn('[mermaid] rendering', els.length, 'diagrams')
        const m = (await import('mermaid')).default
        if (!cancelled) await m.run({ nodes: Array.from(els) as any })
        console.warn('[mermaid] render complete')
      } catch (e) { console.warn('[mermaid] render error:', e) }
    }, 100)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [cot])

  if (loading || !cot || !empresa) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: 'Cotización' }]} />
      <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  const pc = empresa.color_primario || '#1C4587'
  const secciones = Array.isArray(cot.secciones) ? cot.secciones : []

  const estadoBadgeClass: Record<string, string> = {
    borrador: 'badge-borrador', enviada: 'badge-enviada', aceptada: 'badge-aceptada',
    rechazada: 'badge-rechazada', en_negociacion: 'badge-enviada',
  }

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'resumen', label: 'Resumen', icon: <IconBarChart size={14} /> },
    { id: 'tareas', label: `Tareas (${totalTareas})`, icon: <IconCheckCircle size={14} /> },
    { id: 'documento', label: 'Documento', icon: <IconFileText size={14} /> },
  ]

  return (
    <>
    <div className="page-enter">
      {/* ─── Header ─── */}
      <div className="page-header page-header--crumbs">
        <div>
          <Breadcrumbs items={[
            { label: 'Cotizaciones', to: '/cotizaciones' },
            { label: `#${cot.numero} — Propuesta por Tareas` },
          ]} />
          <p className="page-subtitle">
            {cot.cliente_nombre || 'Sin cliente'} —{' '}
            <span className={`badge ${estadoBadgeClass[cot.estado] || ''}`}>{cot.estado}</span>
            <span className="cot-detail__tipo-badge cot-detail__tipo-badge--tareas"><IconCheckCircle size={12} /> Propuesta por Tareas</span>
          </p>
          {cotHijas.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>Cotizaciones generadas:</span>
              {cotHijas.map(h => (
                <Link key={h.id} to={`/cotizaciones/${h.id}`} className="badge badge-aceptada" style={{ fontSize: '0.72rem', textDecoration: 'none' }}>
                  #{h.numero} — {formatCurrency(h.total, h.moneda)}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => nav('/cotizaciones')}><IconArrowLeft size={14} /> Volver</button>
          <Link to={`/tareas-cot/${cot.id}/editar`} className="btn btn-secondary"><IconEdit size={14} /> Editar</Link>
          <button
            className="btn btn-secondary"
            onClick={handleDuplicate}
            disabled={duplicating}
          >
            {duplicating ? 'Duplicando…' : <><IconCopy size={14} /> Duplicar</>}
          </button>
          <button
            className="btn"
            style={{ background: 'var(--primary)', color: '#fff', border: 'none' }}
            onClick={() => setShowGenerarModal(true)}
          >
            <IconFileText size={14} /> Generar Cotización
          </button>
          <button className="btn btn-primary" onClick={exportPDF} disabled={exportStep >= 0}>
            <IconDownload size={14} /> {exportStep >= 0 ? `Exportando (${exportStep + 1}/5)` : 'Exportar PDF'}
          </button>
          <button
            className="btn"
            style={{ background: '#25D366', color: '#fff', border: 'none' }}
            onClick={() => { setWaPhone(cot.cliente_telefono || ''); setShowWAModal(true) }}
            disabled={sendingWA}
          >
            {sendingWA ? 'Enviando…' : <><IconWhatsApp size={14} /> Enviar WhatsApp</>}
          </button>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div className="cot-detail-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`cot-detail-tab ${activeTab === t.id ? 'cot-detail-tab--active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <span className="cot-detail-tab__icon">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── Tab: Resumen ─── */}
      {activeTab === 'resumen' && (
        <div className="cot-detail-content" style={{ animation: 'fadeIn 0.3s ease' }}>
          {/* KPI Cards */}
          <div className="cot-detail-kpis">
            <div className="cot-detail-kpi">
              <div className="cot-detail-kpi__icon"><IconList size={24} /></div>
              <div className="cot-detail-kpi__value">{totalTareas}</div>
              <div className="cot-detail-kpi__label">Tareas</div>
            </div>
            <div className="cot-detail-kpi">
              <div className="cot-detail-kpi__icon"><IconTimer size={24} /></div>
              <div className="cot-detail-kpi__value">{totalHoras}h</div>
              <div className="cot-detail-kpi__label">Horas estimadas</div>
            </div>
            <div className="cot-detail-kpi">
              <div className="cot-detail-kpi__icon"><IconZap size={24} /></div>
              <div className="cot-detail-kpi__value">{r2(totalTokens)}M</div>
              <div className="cot-detail-kpi__label">Tokens totales</div>
            </div>
            <div className="cot-detail-kpi cot-detail-kpi--highlight">
              <div className="cot-detail-kpi__icon"><IconDollar size={24} /></div>
              <div className="cot-detail-kpi__value">{formatCurrency(cot.total, cot.moneda)}</div>
              <div className="cot-detail-kpi__label">Total general</div>
            </div>
          </div>

          <div className="cot-detail-grid">
            {/* Client Data */}
            <div className="card">
              <div className="card-header"><h3><IconUser size={16} /> Datos del Cliente</h3></div>
              <div className="cot-detail-info">
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Empresa</span><span>{cot.cliente_nombre || '—'}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Contacto</span><span>{cot.cliente_contacto || '—'}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">RUC/DNI</span><span className="cot-detail-info__mono">{cot.cliente_ruc || '—'}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Teléfono</span><span>{cot.cliente_telefono || '—'}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Email</span><span>{cot.cliente_email || '—'}</span></div>
              </div>
            </div>

            {/* Project + Conditions */}
            <div className="card">
              <div className="card-header"><h3><IconPackage size={16} /> Proyecto y Condiciones</h3></div>
              <div className="cot-detail-info">
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Proyecto</span><span style={{ fontWeight: 600 }}>{cot.proyecto_nombre || '—'}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Fecha emisión</span><span>{formatDate(cot.fecha_emision)}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Validez</span><span>{cot.validez_dias} días calendario</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Moneda</span><span className="badge badge-enviada" style={{ fontSize: '0.72rem' }}>{cot.moneda}</span></div>
                <div className="cot-detail-info__row"><span className="cot-detail-info__label">Estado</span><span className={`badge ${estadoBadgeClass[cot.estado] || ''}`}>{cot.estado}</span></div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="card" style={{ gridColumn: 'span 2' }}>
              <div className="card-header"><h3><IconWallet size={16} /> Resumen Financiero</h3></div>
              <div className="cot-detail-financials">
                <div className="cot-detail-financials__grid">
                  {tareasItems.filter(t => t.titulo.trim()).map((t, i) => (
                    <div key={i} className="cot-detail-financials__item">
                      <span className="cot-detail-financials__item-num">{i + 1}.</span>
                      <span className="cot-detail-financials__item-name">{t.titulo}</span>
                      <span className="cot-detail-financials__item-amount">{formatCurrency(t.monto, cot.moneda)}</span>
                    </div>
                  ))}
                </div>
                <div className="cot-detail-financials__totals">
                  <div className="cot-detail-financials__total-row"><span>Subtotal</span><span>{formatCurrency(cot.subtotal, cot.moneda)}</span></div>
                  <div className="cot-detail-financials__total-row"><span>IGV ({cot.igv_porcentaje}%)</span><span>{formatCurrency(cot.igv_monto, cot.moneda)}</span></div>
                  {cot.descuento_porcentaje > 0 && (
                    <div className="cot-detail-financials__total-row cot-detail-financials__total-row--discount"><span>Descuento ({fmtPct(cot.descuento_porcentaje)}%)</span><span>-{formatCurrency(cot.descuento_monto, cot.moneda)}</span></div>
                  )}
                  <div className="cot-detail-financials__total-row cot-detail-financials__total-row--total"><span>TOTAL</span><span>{formatCurrency(cot.total, cot.moneda)}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {cot.notas && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header"><h3><IconEdit size={16} /> Notas</h3></div>
              <div style={{ padding: '12px 16px', fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{cot.notas}</div>
            </div>
          )}
        </div>
      )}

      {/* ─── Tab: Tareas ─── */}
      {activeTab === 'tareas' && (
        <div className="cot-detail-content" style={{ animation: 'fadeIn 0.3s ease' }}>
          <div className="card">
            <div className="card-header">
              <h3>Desglose de Tareas</h3>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{totalTareas} tarea{totalTareas !== 1 ? 's' : ''} · Clic para expandir detalle</span>
            </div>
            <div className="table-container" style={{ border: 'none' }}>
              <table className="tarea-detail-table">
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th style={{ width: '35%' }}>Tarea</th>
                    <th style={{ width: '12%' }}>Horas</th>
                    <th style={{ width: '12%' }}>Tokens</th>
                    <th style={{ width: '18%' }}>Monto</th>
                    <th style={{ width: '10%' }}>Entreg.</th>
                    <th style={{ width: '8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {tareasItems.map((tarea, idx) => (
                    <TareaDetalleInline
                      key={idx}
                      tarea={tarea}
                      idx={idx}
                      moneda={cot.moneda}
                      isExpanded={expandedTarea === idx}
                      onToggle={() => setExpandedTarea(expandedTarea === idx ? null : idx)}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="tarea-detail-table__footer">
                    <td></td>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>{totalHoras}h</strong></td>
                    <td><strong>{r2(totalTokens)}M</strong></td>
                    <td><strong>{formatCurrency(cot.subtotal, cot.moneda)}</strong></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {(activeTab === 'documento' || exportStep >= 0 || sendingWA) && (
        <div className={`cot-detail-content cot-detail-content--documento ${activeTab !== 'documento' ? 'cot-detail-content--hidden' : ''}`}>
          <div ref={pdfRef} id="cotizacion-pdf-content">
            {/* Render the same PDF preview pages as VerCotizacion */}
            {renderPDFPreview(cot, empresa, tareasItems, secciones, pc)}
          </div>
        </div>
      )}

      {/* ─── Export Progress Modal ─── */}
      {exportStep >= 0 && (
        <div className="modal-backdrop">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3>Exportando Cotización</h3>
            </div>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              {exportStep < 4 && <div className="export-spinner" />}
              <div className="export-step-counter">{exportStep + 1}/5</div>
              <div className="export-step-label">
                {['Generando PDF…', 'Subiendo a Storage…', 'Guardando referencia…', 'Descargando…', 'Completado ✓'][exportStep]}
              </div>
              <div className="export-progress-bar">
                <div className="export-progress-fill" style={{ width: `${((exportStep + 1) / 5) * 100}%` }} />
              </div>
            </div>
            {exportStep < 4 && (
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { cancelledRef.current = true }}>Cancelar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── WhatsApp Modal ─── */}
      {showWAModal && (
        <div className="modal-backdrop" onClick={() => !sendingWA && setShowWAModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>Enviar Cotización por WhatsApp</h3>
              <button className="modal-close" onClick={() => !sendingWA && setShowWAModal(false)}>×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Se enviará la cotización <strong>{cot.numero}</strong> con el PDF adjunto al número indicado.
              </p>
              <label className="form-label">Teléfono (con código de país)</label>
              <input type="tel" className="form-control" placeholder="51929099060" value={waPhone} onChange={e => setWaPhone(e.target.value)} disabled={sendingWA} autoFocus />
              <p style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>Ejemplo: 51929099060 (Perú) — sin + ni espacios</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowWAModal(false)} disabled={sendingWA}>Cancelar</button>
              <button className="btn" style={{ background: '#25D366', color: '#fff', border: 'none' }} onClick={sendWhatsApp} disabled={sendingWA || !waPhone.trim()}>
                {sendingWA ? 'Enviando…' : <><IconFileText size={14} /> Enviar con PDF</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Generar Cotización Modal */}
    <GenerarCotizacionModal
      isOpen={showGenerarModal}
      onClose={() => setShowGenerarModal(false)}
      cotOrigen={cot}
      tareas={tareasItems}
      moneda={cot.moneda}
    />
    </>
  )
}

/* ─── PDF Preview Renderer (extracted from VerCotizacion) ─── */
function renderPDFPreview(
  cot: Cotizacion, empresa: Empresa, tareasItems: CotizacionTarea[],
  secciones: { titulo: string; contenido: string }[], pc: string
) {
  const ROWS_FIRST_PAGE = 4
  const ROWS_CONT_PAGE = 6
  const tareasChunks: CotizacionTarea[][] = []
  if (tareasItems.length > ROWS_FIRST_PAGE) {
    tareasChunks.push(tareasItems.slice(0, ROWS_FIRST_PAGE))
    let remaining = tareasItems.slice(ROWS_FIRST_PAGE)
    while (remaining.length > 0) {
      tareasChunks.push(remaining.slice(0, ROWS_CONT_PAGE))
      remaining = remaining.slice(ROWS_CONT_PAGE)
    }
  }
  const needsPagination = tareasChunks.length > 1
  const firstPageTareas = needsPagination ? tareasChunks[0] : tareasItems

  const getTotalMTokLocal = (t: CotizacionTarea): number | null => {
    if (t.costos_items?.length) {
      const total = t.costos_items
        .filter(c => c.unidad === 'MTok')
        .reduce((sum, c) => sum + c.cantidad, 0)
      return total > 0 ? r2(total) : null
    }
    if (!t.costos) return null
    const total = t.costos.tokens_input_mtok + t.costos.tokens_thinking_mtok + t.costos.tokens_output_mtok
    return total > 0 ? r2(total) : null
  }

  const renderTareaRow = (t: CotizacionTarea, globalIdx: number) => {
    const totalMTok = getTotalMTokLocal(t)
    return (
      <tr key={globalIdx}>
        <td style={{ fontWeight: 700, color: '#9ca3af' }}>{globalIdx + 1}</td>
        <td>
          <a className="cot-task-link" onClick={() => document.getElementById(`tarea-${globalIdx}`)?.scrollIntoView({ behavior: 'smooth' })}>{t.titulo}</a>
          {t.descripcion_corta && <div style={{ fontSize: '8pt', color: '#6b7280', fontWeight: 400, marginTop: 2 }}>{t.descripcion_corta}</div>}
          <a className="cot-task-view-link" onClick={() => document.getElementById(`tarea-${globalIdx}`)?.scrollIntoView({ behavior: 'smooth' })}>Ver detalle →</a>
        </td>
        <td>{t.horas_estimadas ? `${t.horas_estimadas}h` : '—'}</td>
        <td>{totalMTok !== null ? `${totalMTok}M` : '—'}</td>
        <td>{formatCurrency(t.monto, cot.moneda)}</td>
      </tr>
    )
  }

  const tareasTableHead = (
    <thead><tr><th style={{ width: '6%' }}>#</th><th style={{ width: '42%' }}>Tarea</th><th style={{ width: '14%' }}>Horas</th><th style={{ width: '18%' }}>Tokens</th><th style={{ width: '20%' }}>Monto<br/><span style={{ fontSize: '7pt', fontWeight: 400, color: '#9ca3af' }}>s/IGV</span></th></tr></thead>
  )

  const renderBankInfo = () => (
    <DatosBancarios empresa={empresa} moneda={cot.moneda} />
  )

  return (
    <>
      {/* COVER PAGE */}
      <div className="cotizacion-preview">
        <div className="cot-header" style={{ background: `linear-gradient(135deg, ${pc} 0%, ${pc}cc 100%)` }}>
          <div className="cot-header-left">
            {empresa.logo_url ? <img src={empresa.logo_url} className="cot-logo" alt={empresa.nombre ?? ''} /> : <div className="cot-logo-text">{empresa.nombre || '1Conecta'}</div>}
          </div>
          <div className="cot-header-right">
            <div className="cot-header-fiscal">{empresa.razon_social || ''}</div>
            <div>RUC {empresa.ruc || ''}</div>
          </div>
        </div>
        <div className="cot-title-banner" style={{ borderLeft: `4px solid ${pc}` }}>
          <div className="cot-title-label">PROPUESTA DE SERVICIOS POR TAREA</div>
        </div>
        <div className="cot-meta">
          <div className="cot-meta-block">
            <h4>Cotizado a</h4>
            <div className="cot-meta-grid">
              <span className="cot-meta-key">E:</span><span>{cot.cliente_nombre || '—'}</span>
              <span className="cot-meta-key">C:</span><span>{cot.cliente_contacto || '—'}</span>
              <span className="cot-meta-key">T:</span><span>{cot.cliente_telefono || '—'}</span>
              <span className="cot-meta-key">@:</span><span>{cot.cliente_email || '—'}</span>
            </div>
          </div>
          <div className="cot-meta-block" style={{ textAlign: 'right' }}>
            <h4>Detalles</h4>
            <div className="cot-meta-grid" style={{ justifyItems: 'end' }}>
              <span className="cot-meta-key">N:</span><span>{cot.numero}</span>
              <span className="cot-meta-key">F:</span><span>{formatDate(cot.fecha_emision)}</span>
              <span className="cot-meta-key">D:</span><span>Validez: {cot.validez_dias} días calendario</span>
            </div>
          </div>
        </div>
        <div className="cot-body">
          <p className="cot-intro">A través del presente documento, ponemos a su disposición la propuesta económica correspondiente. En las siguientes secciones encontrará el desglose detallado de cada requerimiento y la inversión asociada.</p>
          <div className="cot-tareas-summary" id="resumen">
            <h3 className="cot-section-title" style={{ borderColor: pc }}>Desglose de Tareas</h3>
            <table className="cot-table">
              {tareasTableHead}
              <tbody>{firstPageTareas.map((t, i) => renderTareaRow(t, i))}</tbody>
            </table>
            {!needsPagination && secciones.length > 0 && (
              <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, cronogramas, entregables y el desglose detallado, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>
            )}
          </div>
        </div>
        <div className="cot-footer" style={{ background: pc }}>
          <span>{empresa.nombre || '1Conecta'}</span>
          <span>{empresa.ubicacion || ''} | {empresa.telefono || ''}</span>
          <span>{empresa.website || ''}</span>
        </div>
      </div>

      {/* CONTINUATION PAGES */}
      {needsPagination && tareasChunks.slice(1).map((chunk, chunkIdx) => {
        const globalOffset = ROWS_FIRST_PAGE + chunkIdx * ROWS_CONT_PAGE
        const isLastChunk = chunkIdx === tareasChunks.length - 2
        const mergeBankInfo = isLastChunk && chunk.length <= 4
        return (
          <div key={`cont-${chunkIdx}`} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h3 className="cot-section-title" style={{ borderColor: pc }}>Desglose de Tareas (cont.)</h3>
              <table className="cot-table">{tareasTableHead}<tbody>{chunk.map((t, i) => renderTareaRow(t, globalOffset + i))}</tbody></table>
              {isLastChunk && secciones.length > 0 && <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, cronogramas, entregables y el desglose detallado, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>}
              {mergeBankInfo && <><h3 className="cot-section-title" style={{ borderColor: pc, marginTop: 24 }}>Datos de Pago</h3><div className="cot-bottom-row">{renderBankInfo()}</div><div className="cot-validez">Esta cotización tiene una validez de {cot.validez_dias} días calendario a partir de la fecha de emisión.</div></>}
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre || '1Conecta'}</span><span>Pág. {chunkIdx + 2}</span><span>{empresa.website || ''}</span></div>
          </div>
        )
      })}

      {/* BANK INFO PAGE */}
      {(() => {
        const totalRows = tareasItems.length
        const needsPag = totalRows > ROWS_FIRST_PAGE
        const lastChunkSize = needsPag ? ((totalRows - ROWS_FIRST_PAGE) % ROWS_CONT_PAGE || ROWS_CONT_PAGE) : 0
        const wasMerged = needsPag && lastChunkSize <= 4
        if (wasMerged) return null
        return (
          <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h3 className="cot-section-title" style={{ borderColor: pc }}>Datos de Pago</h3>
              <div className="cot-bottom-row">{renderBankInfo()}</div>
              <div className="cot-validez">Esta cotización tiene una validez de {cot.validez_dias} días calendario a partir de la fecha de emisión.</div>
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre || '1Conecta'}</span><span>Pág. 2</span><span>{empresa.website || ''}</span></div>
          </div>
        )
      })()}

      {/* SECTION PAGES */}
      {secciones.length > 0 && (
        <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
          <div className="cot-page-header" style={{ background: pc }} />
          <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
          <div className="cot-body" style={{ paddingTop: 40 }}>
            <h2 className="cot-page-title">Cotización de Servicios</h2>
            <div className="cot-index-meta">
              <p>Fecha de Emisión: <em>{formatDate(cot.fecha_emision)}</em></p>
              <p>Proyecto/Servicio: <em>{cot.proyecto_nombre || 'Propuesta de Servicios'}</em></p>
            </div>
            <div className="cot-index-box">
              <h3>Índice</h3>
              {secciones.map((s, i) => (
                <div key={i} className="cot-index-item"><strong>{s.titulo}</strong><span className="cot-index-dots" /><span className="cot-index-page">{i + 3}</span></div>
              ))}
            </div>
          </div>
          <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre}</span><span>Pág. 2</span><span>{empresa.website}</span></div>
        </div>
      )}
      {secciones.map((sec, idx) => (
        <div key={idx} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
          <div className="cot-page-header" style={{ background: pc }} />
          <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
          <div className="cot-body">
            <h3 className="cot-section-title" style={{ borderColor: pc }}>{sec.titulo}</h3>
            <div className="cot-section-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(processContent(sec.contenido)) }} />
          </div>
          <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre}</span><span>Pág. {idx + 3}</span><span>{empresa.website}</span></div>
        </div>
      ))}

      {/* TASK DETAIL PAGES */}
      {tareasItems.map((tarea, idx) => {
        const baseOffset = 2 + (secciones.length > 0 ? 1 + secciones.length : 0)
        const startPage = baseOffset + idx * 3 + 1
        return (
          <TaskDetailPages
            key={`tarea-${idx}`}
            tarea={tarea}
            idx={idx}
            totalTareas={tareasItems.length}
            pc={pc}
            moneda={cot.moneda}
            empresaNombre={empresa.nombre || '1Conecta'}
            empresaWebsite={empresa.website || ''}
            clienteRuc={cot.cliente_ruc || ''}
            startPage={startPage}
            formatCurrency={formatCurrency}
            processContent={processContent}
          />
        )
      })}
    </>
  )
}
